export type CompiledAppAnalytics = {
  provider: 'ga4' | 'gtm' | 'clarity';
  key: string;
};

/**
 * The key comes from tenant-admin configuration, so it is only used when it has the exact
 * shape of the provider's own id. Anything else is never put into a script URL.
 */
const KEY_PATTERNS: Record<CompiledAppAnalytics['provider'], RegExp> = {
  ga4: /^G-[A-Z0-9]+$/,
  gtm: /^GTM-[A-Z0-9]+$/,
  clarity: /^[a-z0-9]{6,20}$/,
};

let activeAnalytics: CompiledAppAnalytics | null = null;

function isDevBuild(): boolean {
  return typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);
}

function appendScript(id: string, src: string): void {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function injectGa4(measurementId: string): void {
  if (isDevBuild() && !(window as Window & { __ip4xGa4FetchLog?: boolean }).__ip4xGa4FetchLog) {
    (window as Window & { __ip4xGa4FetchLog?: boolean }).__ip4xGa4FetchLog = true;
    const fetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const url = String(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
      if (url.includes('google-analytics.com')) {
        console.debug('[ga4 outbound] fetch', url);
      }
      return fetch(input, init);
    };
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // gtag.js only recognises an `arguments` object in the data layer, not a rest array.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }

  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', measurementId, isDevBuild() ? { debug_mode: true } : {});
  appendScript('ip4x-ga4', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
}

function injectGtm(containerId: string): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  appendScript('ip4x-gtm', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);
}

function injectClarity(projectId: string): void {
  // Clarity's queue stub; calls made before the tag loads are replayed from `q`.
  const stub: ClarityStub = function () {
    // eslint-disable-next-line prefer-rest-params
    (stub.q ??= []).push(arguments);
  };
  window.clarity ??= stub;
  appendScript('ip4x-clarity', `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`);
}

export function getActiveAppAnalytics(): CompiledAppAnalytics | null {
  return activeAnalytics;
}

export function injectAppAnalytics(analytics?: CompiledAppAnalytics | null): void {
  if (!analytics?.provider || !KEY_PATTERNS[analytics.provider].test(analytics.key)) {
    activeAnalytics = null;
    return;
  }

  // Track the active provider so UI (e.g. banner-click forwarding) stays aware,
  // but never inject the third-party tag during local development. Clarity's
  // session recording in particular floods the network with `collect` requests
  // and makes the app laggy while developing.
  activeAnalytics = analytics;
  if (isDevBuild()) {
    console.debug('[analytics] skipped in dev', analytics.provider);
    return;
  }

  const marker = `${analytics.provider}:${analytics.key}`;
  if (document.documentElement.dataset.ip4xAnalyticsInjected === marker) {
    activeAnalytics = analytics;
    return;
  }

  document.documentElement.dataset.ip4xAnalyticsInjected = marker;
  activeAnalytics = analytics;

  switch (analytics.provider) {
    case 'ga4':
      injectGa4(analytics.key);
      break;
    case 'gtm':
      injectGtm(analytics.key);
      break;
    case 'clarity':
      injectClarity(analytics.key);
      break;
  }
}

type ClarityStub = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: ClarityStub;
  }
}
